import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import moment from "moment";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { PageHeader, SectionCard } from "_shared/ui";
import { fetchOfficersList } from "slices/officers";
import authAxios from "setups/axios";
import { toastMessage } from "utils/toastMessage";

import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Leaflet's default marker icons reference assets by URL and break under
// webpack. Re-point them at the bundled images.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Sensible fallback when the SO has no plottable dealers (center of India).
const DEFAULT_CENTER = [22.9734, 78.6569];
const DEFAULT_ZOOM = 5;

// Shared search-field styling: sits on the paper surface, with the JK-gold
// brand accent on hover/focus (border + label). Stays square per the theme.
const searchFieldSx = {
  bgcolor: "background.paper",
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
    borderWidth: 2,
  },
  "& label.Mui-focused": { color: "primary.main" },
};

// Levenshtein edit distance (iterative, two-row) — used for typo tolerance.
const levenshtein = (a, b) => {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[n];
};

// Dependency-free fuzzy matcher. Returns a score (higher = better) or -1 for
// no match. A contiguous substring ranks highest; otherwise the query must
// appear as an in-order subsequence (gaps/word jumps allowed), with bonuses
// for consecutive chars and word-boundary starts. Finally a small edit-distance
// check lets single-character typos still match.
const fuzzyScore = (text, query) => {
  const t = String(text || "").toLowerCase();
  const q = String(query || "").toLowerCase().trim();
  if (!q) return 0;
  if (!t) return -1;

  // Strongest signal: contiguous substring (earlier + tighter scores higher).
  const idx = t.indexOf(q);
  if (idx !== -1) return 1000 - idx - (t.length - q.length);

  // In-order subsequence match with positional bonuses.
  let score = 0;
  let cursor = 0;
  let prevMatch = -2;
  let matched = 0;
  for (let qi = 0; qi < q.length; qi++) {
    let found = -1;
    for (let k = cursor; k < t.length; k++) {
      if (t[k] === q[qi]) {
        found = k;
        break;
      }
    }
    if (found === -1) break;
    matched++;
    score += 10;
    if (found === prevMatch + 1) score += 15; // consecutive run
    if (found === 0 || /[\s\-_/]/.test(t[found - 1])) score += 10; // word start
    prevMatch = found;
    cursor = found + 1;
  }
  if (matched === q.length) return score;

  // Typo tolerance: compare the query against each word (and its leading slice).
  const tolerance = q.length <= 4 ? 1 : 2;
  let best = Infinity;
  for (const token of t.split(/[\s\-_/]+/)) {
    if (!token) continue;
    best = Math.min(best, levenshtein(q, token));
    if (token.length > q.length) {
      best = Math.min(best, levenshtein(q, token.slice(0, q.length)));
    }
  }
  if (best <= tolerance) return 5 - best;

  return -1;
};

// Ranks items by the best fuzzy score across the given fields; drops non-matches.
const fuzzyRank = (items, query, fields) => {
  const q = query.trim();
  if (!q) return items;
  return items
    .map((item) => ({
      item,
      score: Math.max(...fields.map((f) => fuzzyScore(f(item), q))),
    }))
    .filter((x) => x.score >= 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.item);
};

const hasCoords = (d) =>
  Number.isFinite(d.latitude) &&
  Number.isFinite(d.longitude) &&
  d.latitude !== 0 &&
  d.longitude !== 0;

// Recenters / fits the map to the current markers whenever they change.
// MapContainer only reads `center`/`zoom` once, so re-centering needs the map
// instance via useMap.
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else if (points.length > 1) {
      map.fitBounds(points, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
};

const Main = () => {
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.user);
  const { officers } = useSelector((state) => state.officers);

  const [selectedSO, setSelectedSO] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [dealerSearch, setDealerSearch] = useState("");

  useEffect(() => {
    if (!user?._id) return;
    dispatch(fetchOfficersList(user.SAP_ID));
  }, [user]);

  const soOptions = useMemo(
    () => (officers || []).filter((o) => o?.SAP_ID),
    [officers]
  );

  const validDealers = useMemo(
    () => (dealers || []).filter(hasCoords),
    [dealers]
  );

  const filteredDealers = useMemo(
    () =>
      fuzzyRank(validDealers, dealerSearch, [
        (d) => d.name,
        (d) => d.dealerCode,
      ]),
    [validDealers, dealerSearch]
  );

  const points = useMemo(
    () => filteredDealers.map((d) => [d.latitude, d.longitude]),
    [filteredDealers]
  );

  const loadDealers = async (sapId) => {
    setLoading(true);
    try {
      const res = await authAxios().get(
        `/api/v1/dash/dealers/map-locations?sapId=${sapId}`
      );
      setDealers(res.data?.data ?? []);
    } catch (e) {
      setDealers([]);
      toastMessage(
        e?.response?.data?.message ?? "Failed to load dealer locations",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const soLabel = (o) => (o.name ? `${o.name} — ${o.SAP_ID}` : `${o.SAP_ID}`);

  const handleSOChange = (event, option) => {
    setSelectedSO(option);
    setDealerSearch("");
    if (option?.SAP_ID) loadDealers(option.SAP_ID);
    else setDealers([]);
  };

  const renderMap = () => {
    if (!selectedSO) {
      return (
        <Box
          sx={{
            height: 560,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Select a Sales Officer to view their dealers on the map.
          </Typography>
        </Box>
      );
    }

    if (loading) {
      return (
        <Box
          sx={{
            height: 560,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (!validDealers.length) {
      return (
        <Box
          sx={{
            height: 560,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No mapped dealers for this SO.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ height: 560, width: "100%" }}>
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points} />
          {filteredDealers.map((d) => (
            // Include showLabels in the key so the marker (and its tooltip)
            // remounts when the toggle flips — react-leaflet only applies a
            // tooltip's `permanent` option at creation, not on prop change.
            <Marker
              key={`${d._id}-${showLabels}`}
              position={[d.latitude, d.longitude]}
            >
              <Tooltip permanent={showLabels} direction="top" offset={[0, -10]}>
                <strong>{d.name || "-"}</strong> ({d.dealerCode || "-"})
              </Tooltip>
              <Popup>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {d.name || "-"}
                </Typography>
                <Typography variant="body2">Code: {d.dealerCode || "-"}</Typography>
                <Typography variant="body2">City: {d.city || "-"}</Typography>
                <Typography variant="body2">{d.address || "-"}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Updated:{" "}
                  {d.locUpdatedOn
                    ? moment(d.locUpdatedOn).format("DD MMM YYYY")
                    : "-"}
                </Typography>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </Box>
    );
  };

  return (
    <>
      <PageHeader
        title="Dealer Map"
        subtitle="Plot a Sales Officer's dealers on the map"
      />

      <SectionCard
        title="Locations"
        disablePadding
        action={
          <>
            <Autocomplete
              size="small"
              options={soOptions}
              value={selectedSO}
              onChange={handleSOChange}
              getOptionLabel={soLabel}
              isOptionEqualToValue={(opt, val) => opt.SAP_ID === val.SAP_ID}
              filterOptions={(opts, { inputValue }) =>
                fuzzyRank(opts, inputValue, [(o) => o.SAP_ID, (o) => o.name])
              }
              noOptionsText="No Sales Officers found"
              sx={{ minWidth: 280 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sales Officer"
                  placeholder="Search by SAP ID"
                  sx={searchFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              size="small"
              label="Search dealers"
              placeholder="Name or dealer code"
              value={dealerSearch}
              onChange={(e) => setDealerSearch(e.target.value)}
              disabled={!selectedSO || !validDealers.length}
              sx={{ minWidth: 240, ...searchFieldSx }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showLabels}
                  onChange={(e) => setShowLabels(e.target.checked)}
                />
              }
              label="Always show labels"
            />
          </>
        }
      >
        {renderMap()}
      </SectionCard>
    </>
  );
};

const DealerMap = () => (
  <AccessController roles={["AO", "RO", "ZO", "ADMIN"]}>
    <DashGridL2 Top={TopBar} Main={Main} />
  </AccessController>
);

export default DealerMap;
